import React from 'react';
import { connect } from 'react-redux';
import { Form } from '@ant-design/compatible';
import {
    Drawer,
    Tag,
    Button,
    Row,
    Col,
    Typography,
    Table,
    Spin,
    Divider,
    Space,
    Tooltip,
    Card,
    Collapse,
    Empty,
    Alert,
} from 'antd';
import {
    EditOutlined,
    QuestionCircleOutlined,
    SwapOutlined,
    UndoOutlined,
} from '@ant-design/icons';
import { checkPermission } from '@/utils/permission';
import NumberFormat from 'react-number-format';
import ReactHtmlParser from 'react-html-parser';
import { isMobile } from 'react-device-detect';
import ReceiptForm from './ReceiptForm';
import {
    renderReceiptStatus,
    renderPaymentMethod,
    renderReceiptPaymentType,
    ConvertReceiptType,
    isValidDate,
    renderMoney,
} from '@/utils/helpers';
import { renderReceiptCollectionResult } from '@/utils/receiptCollectionResult';
import PrintButton from '@/components/PrintButton/PrintButton';
import PrintTable from './PrintTable';
import CustomerServiceConvertForm from '@/components/customer-services/CustomerServiceConvertForm';
import { getServiceConversionEligibility } from '@/components/customer-services/customerServiceConversionPresentation';
import { getSpecificService } from '@/redux/actions/customerServicesActions';
import CreatedAtCell from '@/components/CreatedAtCell';
import ImagePreviewList from '@/components/ImagePreviewList';
import moment from 'moment';
import CustomerRefundModal from '@/components/customers/CustomerRefundModal';
import ReceiptHistoryDrawer, {
    ReceiptHistoryButton,
} from './ReceiptHistoryDrawer';
import { getReceiptHistory } from '@/redux/actions/receiptActions';
import {
    buildReceiptContentRows,
    formatConvertedService,
} from './receiptDetailHelpers';

const { Paragraph } = Typography;
const { Panel } = Collapse;

class ReceiptDetail extends React.PureComponent {
    constructor() {
        super();

        this.state = {
            isOpenForm: false,
            isOpenStatusForm: false,
            isOpenConvertForm: false,
            currentService: null,

            // print lazy
            shouldRenderPrint: false,
            isOpenRefund: false,
            isOpenHistory: false,
            historyLoading: false,
            historyItems: [],
        };

        // Cache columns, không tạo lại trong render
        this.contentColumns = this.buildContentColumns();
        this.columnsTransfer = this.buildColumnsTransfer();
        this.financialTimelineColumns = this.buildFinancialTimelineColumns();
    }

    // ----------------- basic UI -----------------
    handleCancel = () => {
        this.props.onCancel();

        // reset lazy flags để lần mở sau nhẹ
        this.setState({
            shouldRenderPrint: false,
            showAllImages: false,
        });
    };

    toggleOpenForm = isOpenForm => {
        this.setState({ isOpenForm });
    };

    toggleOpenUpdateStatusForm = isOpenStatusForm => {
        this.setState({ isOpenStatusForm });
    };

    loadData = () => {
        this.props.reloadData();
    };

    openHistory = () => {
        const receiptId = this.props.currentReceipt?.id;
        if (!receiptId) return;
        this.setState({ isOpenHistory: true, historyLoading: true });
        this.props
            .getReceiptHistory(receiptId)
            .then(historyItems => this.setState({ historyItems }))
            .catch(() => this.setState({ historyItems: [] }))
            .finally(() => this.setState({ historyLoading: false }));
    };

    // ----------------- convert service -----------------
    toggleOpenConvertForm = (isOpenConvertForm, currentService = null) => {
        this.setState({
            isOpenConvertForm,
            currentService,
        });
    };

    onConvert = id => {
        this.props.getSpecificService(id).then(res => {
            this.toggleOpenConvertForm(true, res);
        });
    };

    // ----------------- helpers -----------------
    buildCustomer = currentReceipt => {
        return {
            customer_name: currentReceipt?.customer?.full_name || '',
            customer_phone: Array.isArray(currentReceipt?.customer?.phones)
                ? currentReceipt.customer.phones
                    .map(item => item?.phone)
                    .filter(Boolean)
                    .join(', ')
                : '',
            customer_address: currentReceipt?.customer?.address || '',
            province_name: currentReceipt?.customer?.province?.name || '',
            wards_name: currentReceipt?.customer?.ward?.name || '',
            invoice_no: currentReceipt?.code || '',
            special_discount: currentReceipt?.special_discount ?? 0,
            amount_paid: currentReceipt?.amount_paid ?? 0,
            remaining_debt: currentReceipt?.remaining_debt ?? 0,
            total_amount_paid: currentReceipt?.total_amount_paid ?? 0,
        };
    };

    rowKeyItems = (record, index) => {
        return (
            record?.id ||
            record?.customer_service_id ||
            `${record?.name || 'row'}_${index}`
        );
    };

    rowKeyTransferItems = (record, index) => {
        return (
            record?.id ||
            record?.code ||
            record?.customer_service_id ||
            `${record?.name || 'row'}_${index}`
        );
    };

    toggleOpenRefund = isOpenRefund => {
        this.setState({ isOpenRefund });
    };

    buildFinancialTimeline = (lineage = {}) => {
        const entryLabels = {
            RECEIPT_OVERPAYMENT: 'Tiền thu chưa phân bổ',
            APPLIED_TO_CUSTOMER_SERVICE: 'Dùng số dư cho dịch vụ',
            CUSTOMER_REFUND: 'Hoàn tiền cho khách',
            RECEIPT_BALANCE_REVERSAL: 'Điều chỉnh do sửa phiếu',
            RECEIPT_DELETE_REVERSAL: 'Hoàn nguồn do hủy phiếu',
            SERVICE_ADJUSTMENT_RELEASE: 'Số dư do điều chỉnh dịch vụ',
            SERVICE_CONVERSION_SURPLUS: 'Tiền thừa từ chuyển đổi dịch vụ',
        };
        const transactionLabels = {
            DEPOSIT_RECEIVED: 'Nhận tiền cọc',
            DEPOSIT_APPLIED_TO_SERVICE: 'Sử dụng cọc cho dịch vụ',
            DEPOSIT_TARGET_CHANGED: 'Đổi dịch vụ dự kiến',
            DEPOSIT_ADJUSTMENT_IN: 'Điều chỉnh tăng tiền cọc',
            DEPOSIT_ADJUSTMENT_OUT: 'Điều chỉnh giảm tiền cọc',
            DEPOSIT_REFUNDED: 'Trả tiền cọc cho khách',
            DEPOSIT_REVERSAL: 'Đảo giao dịch tiền cọc',
        };

        const entries = (lineage.balance_entries || [])
            .filter(
                row =>
                    ![
                        'RECEIPT_OVERPAYMENT',
                        'APPLIED_TO_CUSTOMER_SERVICE',
                        'CUSTOMER_REFUND',
                    ].includes(row.entry_type)
            )
            .map(row => ({
                ...row,
                timeline_key: `entry-${row.id}`,
                event_at: row.effective_at,
                event_label: entryLabels[row.entry_type] || 'Điều chỉnh số dư',
                signed_amount:
                    row.direction === 'DEBIT'
                        ? -Number(row.amount || 0)
                        : Number(row.amount || 0),
            }));

        const applications = (lineage.balance_applications || []).map(row => ({
            ...row,
            timeline_key: `application-${row.id || row.application_id}-${row.source_balance_entry_id
                }`,
            event_at: row.applied_at,
            event_label: 'Đã dùng số dư cho dịch vụ',
            signed_amount: -Number(row.amount || 0),
        }));

        const refunds = (lineage.refund_sources || [])
            .filter(row => row.status === 'COMPLETED')
            .map(row => ({
                ...row,
                timeline_key: `refund-${row.id ||
                    row.refund_id}-${row.source_balance_entry_id ||
                    row.source_allocation_id}`,
                event_at: row.refund_at,
                event_label: 'Đã hoàn tiền cho khách',
                signed_amount: -Number(row.amount || 0),
            }));

        const deposits = (lineage.deposit_transactions || [])
            .filter(row => row.transaction_type !== 'DEPOSIT_RECEIVED')
            .map(row => ({
                ...row,
                timeline_key: `deposit-${row.id}`,
                event_at: row.effective_at,
                event_label:
                    transactionLabels[row.transaction_type] ||
                    'Giao dịch tiền cọc',
                signed_amount:
                    row.direction === 'DEBIT'
                        ? -Number(row.amount || 0)
                        : row.direction === 'CREDIT'
                            ? Number(row.amount || 0)
                            : 0,
            }));

        return [
            ...entries,
            ...applications,
            ...refunds,
            ...deposits,
        ].sort((a, b) =>
            String(b.event_at || '').localeCompare(String(a.event_at || ''))
        );
    };

    getRefundedAmount = (lineage = {}) => {
        return (lineage.refund_sources || [])
            .filter(row => row.status === 'COMPLETED')
            .reduce((sum, row) => sum + Number(row?.amount || 0), 0);
    };

    buildAllocationRows = (lineage = {}) => {
        const completedRefunds = (lineage.refund_sources || [])
            .filter(row => row.status === 'COMPLETED')
            .reduce((result, row) => {
                const allocationId = Number(row.source_allocation_id || 0);
                if (allocationId) {
                    result[allocationId] =
                        (result[allocationId] || 0) + Number(row.amount || 0);
                }
                return result;
            }, {});

        return (lineage.allocations || [])
            .map(row => {
                const allocatedAmount = Number(row.allocated_amount || 0);
                const refundedAmount = completedRefunds[Number(row.id)] || 0;

                return {
                    ...row,
                    allocated_amount: allocatedAmount,
                    refunded_amount: refundedAmount,
                    remaining_amount: Math.max(
                        0,
                        allocatedAmount - refundedAmount
                    ),
                };
            })
            .filter(row => row.allocated_amount > 0);
    };

    getAllocationSourceTotal = (allocationRows, origin) =>
        allocationRows
            .filter(row => row.allocation_origin === origin)
            .reduce((sum, row) => sum + Number(row.remaining_amount || 0), 0);

    renderSectionHeader = (title, description = null, extra = null) => (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 8,
            }}
        >
            <div style={{ minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 18,
                        lineHeight: '24px',
                        fontWeight: 700,
                        color: 'rgba(0, 0, 0, 0.88)',
                    }}
                >
                    {title}
                </div>

                {description && (
                    <div
                        style={{
                            marginTop: 1,
                            fontSize: 13,
                            lineHeight: '19px',
                            color: 'rgba(0, 0, 0, 0.45)',
                        }}
                    >
                        {description}
                    </div>
                )}
            </div>

            {extra}
        </div>
    );

    renderInfoField = (label, value, options = {}) => {
        const { strong = false, danger = false, compact = false } = options;

        return (
            <div
                style={{
                    minHeight: compact ? 44 : 50,
                    padding: compact ? '2px 0' : '4px 0',
                }}
            >
                <div
                    style={{
                        marginBottom: 1,
                        fontSize: 13,
                        lineHeight: '19px',
                        color: 'rgba(0, 0, 0, 0.45)',
                    }}
                >
                    {label}
                </div>

                <div
                    style={{
                        fontSize: strong ? 17 : 15,
                        lineHeight: strong ? '24px' : '22px',
                        fontWeight: strong ? 700 : 600,
                        color: danger ? '#cf1322' : 'rgba(0, 0, 0, 0.88)',
                        wordBreak: 'break-word',
                    }}
                >
                    {value}
                </div>
            </div>
        );
    };

    renderCommissionProcessingStatus = status => {
        if (status === 'COMPLETED') return <Tag color="green">Hoa hồng đã đồng bộ</Tag>;
        if (status === 'SKIPPED') return <Tag>Không xử lý hoa hồng</Tag>;
        if (['PENDING', 'RUNNING'].includes(status)) return <Tag color="blue">Hoa hồng đang xử lý</Tag>;
        if (status === 'WAITING') return <Tag color="orange">Hoa hồng chờ nghiệp vụ</Tag>;
        if (status === 'FAILED_RETRY') return <Tag color="volcano">Hoa hồng chờ thử lại</Tag>;
        return null;
    };

    renderFinancialMetric = (label, value, options = {}) => {
        const {
            primary = false,
            danger = false,
            positive = false,
            muted = false,
        } = options;

        let valueColor = 'rgba(0, 0, 0, 0.88)';

        if (danger) {
            valueColor = '#cf1322';
        } else if (positive) {
            valueColor = '#237804';
        } else if (muted) {
            valueColor = 'rgba(0, 0, 0, 0.65)';
        }

        return (
            <div
                style={{
                    height: '100%',
                    minHeight: primary ? 86 : 76,
                    padding: primary ? '10px 12px' : '8px 10px',
                    border: primary ? '1px solid #d9e8ff' : '1px solid #f0f0f0',
                    borderRadius: 8,
                    background: primary ? '#f7fbff' : '#fafafa',
                }}
            >
                <div
                    style={{
                        marginBottom: 3,
                        fontSize: 13,
                        lineHeight: '19px',
                        fontWeight: 500,
                        color: 'rgba(0, 0, 0, 0.45)',
                    }}
                >
                    {label}
                </div>

                <div
                    style={{
                        fontSize: primary ? 22 : 18,
                        lineHeight: primary ? '30px' : '26px',
                        fontWeight: 700,
                        color: valueColor,
                        wordBreak: 'break-word',
                    }}
                >
                    {value}
                </div>
            </div>
        );
    };

    renderMiniFinancialField = (label, value, options = {}) => {
        const { danger = false, strong = false } = options;

        return (
            <div
                style={{
                    padding: '6px 0',
                    minHeight: 52,
                }}
            >
                <div
                    style={{
                        marginBottom: 2,
                        fontSize: 13,
                        lineHeight: '19px',
                        color: 'rgba(0, 0, 0, 0.45)',
                    }}
                >
                    {label}
                </div>

                <div
                    style={{
                        fontSize: strong ? 18 : 16,
                        lineHeight: '24px',
                        fontWeight: 700,
                        color: danger ? '#cf1322' : 'rgba(0, 0, 0, 0.88)',
                    }}
                >
                    {value}
                </div>
            </div>
        );
    };

    // ----------------- columns -----------------
    buildContentColumns = () => [
        {
            title: 'Sản phẩm/Dịch vụ',
            dataIndex: 'name',
            width: 250,
            render: (value, record) => {
                const convertedServices = (record.converted_to_services || [])
                    .map(formatConvertedService)
                    .filter(Boolean);
                const currentName = record?.current_service_financial?.name;
                const snapshotName = typeof value === 'string' ? value.trim() : '';

                return (
                    <div>
                        <div style={{ fontWeight: 600 }}>
                            {snapshotName
                                ? snapshotName
                                : 'Nội dung chưa xác định'}
                        </div>
                        {currentName && currentName.trim() !== snapshotName && (
                            <div style={{ color: '#1677ff', fontSize: 12 }}>
                                Hiện tại: {currentName}
                            </div>
                        )}
                        {convertedServices.map((service, index) => (
                            <div
                                key={`${record.id || record.customer_service_id}-converted-${index}`}
                                style={{
                                    marginTop: 2,
                                    fontSize: 12,
                                    lineHeight: '18px',
                                    fontWeight: 400,
                                    color: 'rgba(247, 0, 0, 0.55)',
                                }}
                            >
                                Đã chuyển sang: {service}
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: 'Loại',
            dataIndex: 'type',
            width: 80,
            render: value => (typeof value === 'string' && value ? value : '—'),
        },
        {
            title: 'Số lượng tại phiếu',
            dataIndex: 'quantity',
            width: 80,
            align: 'center',
            render: (value, record) => {
                if (value === null || value === undefined) return '—';
                const current = record?.current_service_financial?.quantity;
                return (
                    <div>
                        <div>{Number(value)}</div>
                        {current !== null && current !== undefined && Number(current) !== Number(value) && (
                            <div style={{ color: '#1677ff', fontSize: 12 }}>Hiện tại: {Number(current)}</div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Giá bán tại phiếu',
            dataIndex: 'price',
            width: 100,
            align: 'right',
            render: (value, record) => {
                if (value === null || value === undefined) return '—';
                const current = record?.current_service_financial?.sale_price;
                return (
                    <div>
                        <div>{renderMoney(Number(value))}</div>
                        {current !== null && current !== undefined && Number(current) !== Number(value) && (
                            <div style={{ color: '#1677ff', fontSize: 12 }}>Hiện tại: {renderMoney(Number(current))}</div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Thành tiền tại phiếu',
            key: 'line_total',
            width: 120,
            align: 'right',
            render: (_, record) => {
                if (record.price === null || record.quantity === null) return '—';
                const snapshotAmount = Number(record.price || 0) * Number(record.quantity || 0);
                const currentAmount = record?.current_service_financial?.amount;
                return (
                    <div>
                        <div>{renderMoney(snapshotAmount)}</div>
                        {currentAmount !== null && currentAmount !== undefined && Number(currentAmount) !== snapshotAmount && (
                            <div style={{ color: '#1677ff', fontSize: 12 }}>
                                Hiện tại: {renderMoney(Number(currentAmount))}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: (
                <Tooltip title="Công nợ của Sản phẩm hoặc Dịch vụ tại thời điểm ngay trước khi phiếu này được phân bổ. Đây là số hệ thống dùng để tính tỷ trọng phân bổ tiền của chính phiếu này.">
                    <span>
                        Số tiền cần thu tại phiếu{' '}
                        <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                    </span>
                </Tooltip>
            ),
            dataIndex: 'amount',
            width: 120,
            align: 'right',
            render: value =>
                value === null || value === undefined ? (
                    '—'
                ) : (
                    <strong>
                        {renderMoney(Math.max(0, Number(value || 0)))}
                    </strong>
                ),
        },
        {
            title: (
                <Tooltip title="Nghĩa vụ ròng và công nợ hiện tại của dịch vụ sau các lần điều chỉnh giá, ưu đãi và phân bổ.">
                    <span>
                        Tài chính hiện tại{' '}
                        <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                    </span>
                </Tooltip>
            ),
            key: 'current_service_financial',
            width: 145,
            align: 'right',
            render: (_, record) => {
                const current = record?.current_service_financial;
                if (!record.customer_service_id || !current) return '—';
                return (
                    <div>
                        <strong>{renderMoney(Math.max(0, Number(current.net_obligation || 0)))}</strong>
                        <div style={{ color: Number(current.outstanding_amount || 0) > 0 ? '#cf1322' : '#389e0d', fontSize: 12 }}>
                            Còn nợ: {renderMoney(Math.max(0, Number(current.outstanding_amount || 0)))}
                        </div>
                    </div>
                );
            },
        },
        {
            title: (
                <Tooltip title="Số tiền thực tế từ phiếu này được gắn cho chính item. Sản phẩm lấy từ product payment lineage; Dịch vụ lấy từ nguồn phân bổ dịch vụ theo tỷ trọng và đều không vượt công nợ tương ứng.">
                    <span>
                        Đã phân bổ{' '}
                        <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                    </span>
                </Tooltip>
            ),
            dataIndex: 'allocated_amount',
            width: 120,
            align: 'right',
            render: (value, record) => (
                <span style={{ fontWeight: 700 }}>
                    {renderMoney(Math.max(0, Number(value || 0)))}
                    {record.refunded_amount > 0 && (
                        <div
                            style={{
                                color: '#cf1322',
                                fontSize: 12,
                                fontWeight: 500,
                            }}
                        >
                            Đã hoàn {renderMoney(record.refunded_amount)}
                        </div>
                    )}
                </span>
            ),
        },
        {
            title: 'Action',
            align: 'center',
            width: 110,
            key: 'actions',
            fixed: 'right',
            render: (_, record) => {
                if (!record.customer_service_id || record.type === 'Sản phẩm') {
                    return null;
                }

                const conversionEligibility = getServiceConversionEligibility(record);
                const isConversionDisabled = !conversionEligibility.canConvert;
                return (
                    checkPermission(
                        'customer_services',
                        'convert_to_service'
                    ) && (
                        <Tooltip
                            title={isConversionDisabled ? conversionEligibility.reason : 'Chuyển đổi dịch vụ sang dịch vụ khác'}
                            placement="left"
                        >
                            <span
                                style={{
                                    cursor: isConversionDisabled
                                        ? 'not-allowed'
                                        : 'pointer',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-block',
                                }}
                            >
                                <span
                                    className={
                                        isConversionDisabled
                                            ? 'disabled-action'
                                            : 'item-action-btn'
                                    }
                                    onClick={
                                        !isConversionDisabled
                                            ? () =>
                                                this.onConvert(
                                                    record.customer_service_id
                                                )
                                            : undefined
                                    }
                                >
                                    <SwapOutlined /> Chuyển đổi
                                </span>
                            </span>
                        </Tooltip>
                    )
                );
            },
        },
    ];

    buildColumnsTransfer = () => {
        return [
            {
                title: 'STT',
                dataIndex: 'stt',
                width: 52,
                align: 'center',
                render: (text, record, index) => index + 1,
            },
            {
                title: 'Mã phiếu',
                dataIndex: 'code',
                key: 'code',
                width: 110,
            },
            {
                title: 'Sản phẩm/dịch vụ',
                dataIndex: 'name',
                width: 310,
                render: (text, record) => {
                    const isTransferred = record.is_transferred;
                    const from = record.converted_from_service_name;
                    const to = record.converted_to_service_name;

                    return (
                        <div>
                            <div
                                style={{
                                    fontSize: 15,
                                    lineHeight: '21px',
                                    fontWeight: 600,
                                    color: 'rgba(0, 0, 0, 0.88)',
                                }}
                            >
                                {text}
                            </div>

                            {(from || (isTransferred && to)) && (
                                <Space
                                    size={[4, 3]}
                                    wrap
                                    style={{ marginTop: 4 }}
                                >
                                    {from && (
                                        <Tooltip
                                            title={`Được chuyển từ: ${from}`}
                                        >
                                            <Tag
                                                color="green"
                                                style={{
                                                    margin: 0,
                                                }}
                                            >
                                                Được chuyển từ: {from}
                                            </Tag>
                                        </Tooltip>
                                    )}

                                    {isTransferred && to && (
                                        <Tooltip
                                            title={`Đã chuyển sang: ${to}`}
                                        >
                                            <Tag
                                                color="red"
                                                style={{
                                                    margin: 0,
                                                }}
                                            >
                                                Đã chuyển sang: {to}
                                            </Tag>
                                        </Tooltip>
                                    )}
                                </Space>
                            )}
                        </div>
                    );
                },
            },
            {
                title: 'Số lượng',
                dataIndex: 'quantity',
                width: 90,
                align: 'center',
            },
            {
                title: 'Đơn giá',
                dataIndex: 'price',
                width: 140,
                align: 'right',
                render: text => (
                    <NumberFormat
                        value={text}
                        displayType="text"
                        suffix="đ"
                        thousandSeparator
                    />
                ),
            },
            {
                title: 'Thành tiền',
                dataIndex: 'line_total',
                width: 145,
                align: 'right',
                render: (_, record) => (
                    <span style={{ fontWeight: 600 }}>
                        <NumberFormat
                            value={
                                Number(record.price || 0) *
                                Number(record.quantity || 0)
                            }
                            displayType="text"
                            suffix="đ"
                            thousandSeparator
                        />
                    </span>
                ),
            },
            {
                title: 'Số tiền chênh lệch',
                dataIndex: 'amount',
                width: 180,
                align: 'right',
                render: (text, record) => {
                    const { amount_to_pay = 0, amount_to_deduct = 0 } = record;

                    if (amount_to_pay > 0) {
                        return (
                            <div
                                style={{
                                    color: '#237804',
                                    fontWeight: 700,
                                    lineHeight: '20px',
                                }}
                            >
                                +{' '}
                                <NumberFormat
                                    value={amount_to_pay}
                                    displayType="text"
                                    thousandSeparator
                                    suffix="đ"
                                />
                                <div
                                    style={{
                                        marginTop: 1,
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }}
                                >
                                    Khách thanh toán thêm
                                </div>
                            </div>
                        );
                    }

                    if (amount_to_deduct > 0) {
                        return (
                            <div
                                style={{
                                    color: '#cf1322',
                                    fontWeight: 700,
                                    lineHeight: '20px',
                                }}
                            >
                                -{' '}
                                <NumberFormat
                                    value={amount_to_deduct}
                                    displayType="text"
                                    thousandSeparator
                                    suffix="đ"
                                />
                                <div
                                    style={{
                                        marginTop: 1,
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }}
                                >
                                    Hoàn lại cho khách
                                </div>
                            </div>
                        );
                    }

                    return (
                        <span
                            style={{
                                color: 'rgba(0, 0, 0, 0.35)',
                            }}
                        >
                            Không chênh lệch
                        </span>
                    );
                },
            },
        ];
    };

    buildFinancialTimelineColumns = () => {
        return [
            {
                title: 'Thời gian',
                dataIndex: 'event_at',
                width: 160,
                render: value =>
                    value ? moment(value).format('DD/MM/YYYY HH:mm') : '—',
            },
            {
                title: 'Loại biến động',
                dataIndex: 'event_label',
                width: 220,
                render: value => (
                    <Tag
                        style={{
                            margin: 0,
                            fontSize: 13,
                        }}
                    >
                        {value}
                    </Tag>
                ),
            },
            {
                title: 'Số tiền',
                dataIndex: 'signed_amount',
                width: 155,
                align: 'right',
                render: value => {
                    const amount = Number(value) || 0;
                    const isNegative = amount < 0;

                    return (
                        <span
                            style={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: isNegative ? '#cf1322' : '#237804',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {`${isNegative ? '-' : '+'}${renderMoney(
                                Math.abs(amount)
                            )}`}
                        </span>
                    );
                },
            },
            {
                title: 'Dịch vụ liên quan',
                dataIndex: 'service_name',
                width: 230,
                render: value =>
                    typeof value === 'string' && value ? value : '—',
            },
            {
                title: 'Người thực hiện',
                dataIndex: 'actor_name',
                width: 175,
                render: value =>
                    typeof value === 'string' && value ? value : 'Hệ thống',
            },
            {
                title: 'Nội dung',
                dataIndex: 'reason',
                width: 280,
                render: value => (typeof value === 'string' ? value : '—'),
            },
        ];
    };

    // ----------------- print lazy -----------------
    ensurePrintRendered = () => {
        if (!this.state.shouldRenderPrint) {
            this.setState({
                shouldRenderPrint: true,
            });
        }
    };

    render() {
        const {
            isOpenForm,
            currentService,
            isOpenConvertForm,
            shouldRenderPrint,
        } = this.state;

        const { visible, currentReceipt, loadingDetail } = this.props;

        const customer = this.buildCustomer(currentReceipt);
        const financialTimeline = this.buildFinancialTimeline(
            currentReceipt?.financial_lineage
        );

        const refundedAmount = this.getRefundedAmount(
            currentReceipt?.financial_lineage
        );
        const refundSummary = currentReceipt?.refund_summary || {
            status: refundedAmount > 0 && refundedAmount >= Number(currentReceipt?.amount_paid || 0)
                ? 'FULL'
                : refundedAmount > 0 ? 'PARTIAL' : 'NONE',
            receipt_amount: Number(currentReceipt?.amount_paid || 0),
            refunded_amount: refundedAmount,
            remaining_amount: Math.max(0, Number(currentReceipt?.amount_paid || 0) - refundedAmount),
            history: [],
        };
        const hasRefund = refundSummary.status !== 'NONE';
        const isFullyRefunded = refundSummary.status === 'FULL';
        const allocationRows = this.buildAllocationRows(
            currentReceipt?.financial_lineage
        );
        const contentRows = buildReceiptContentRows(
            currentReceipt?.items,
            allocationRows
        );
        const balanceUsed = this.getAllocationSourceTotal(
            allocationRows,
            'CUSTOMER_BALANCE'
        );
        const depositUsed = this.getAllocationSourceTotal(
            allocationRows,
            'CUSTOMER_DEPOSIT'
        );
        const isDepositReceipt =
            currentReceipt?.collection_purpose === 'DEPOSIT';
        const currentDebt = Math.max(
            0,
            Number(currentReceipt?.customer_current_finance?.debt_amount) || 0
        );

        const debtAtReceipt = Math.max(
            0,
            Number(currentReceipt?.remaining_debt) || 0
        );
        const receiptNote =
            typeof currentReceipt?.note === 'string'
                ? currentReceipt.note.trim()
                : '';
        const receiptImages = Array.isArray(currentReceipt?.images)
            ? currentReceipt.images
            : [];
        const hasRelatedDetails =
            financialTimeline.length > 0 ||
            (currentReceipt?.transfer_items || []).length > 0;

        const sectionCardStyle = {
            marginBottom: 10,
            borderRadius: 10,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        };

        const cardBodyStyle = {
            padding: isMobile ? 10 : '11px 14px',
        };

        return (
            <>
                <style>
                    {`
                        .receipt-detail-v2 .ant-table {
                            font-size: 14px;
                        }

                        .receipt-detail-v2 .ant-table-thead > tr > th {
                            padding: 9px 10px !important;
                            font-size: 13px;
                            font-weight: 700;
                            color: rgba(0, 0, 0, 0.65);
                            background: #fafafa;
                            white-space: nowrap;
                        }

                        .receipt-detail-v2 .ant-table-tbody > tr > td {
                            padding: 8px 10px !important;
                            vertical-align: top;
                        }

                        .receipt-detail-v2 .ant-collapse {
                            border-radius: 8px;
                            overflow: hidden;
                            background: #fff;
                        }

                        .receipt-detail-v2 .ant-collapse > .ant-collapse-item > .ant-collapse-header {
                            padding: 10px 12px !important;
                            font-size: 15px;
                            font-weight: 600;
                        }

                        .receipt-detail-v2 .ant-collapse-content > .ant-collapse-content-box {
                            padding: 10px 12px !important;
                        }

                        .receipt-detail-v2 .ant-card-body {
                            overflow: hidden;
                        }

                        @media (max-width: 767px) {
                            .receipt-detail-v2 .ant-table {
                                font-size: 13px;
                            }

                            .receipt-detail-v2 .ant-table-thead > tr > th,
                            .receipt-detail-v2 .ant-table-tbody > tr > td {
                                padding: 7px 8px !important;
                            }
                        }
                    `}
                </style>

                <Drawer
                    visible={visible}
                    title={`Chi tiết phiếu thu: #${currentReceipt?.id || ''}`}
                    onClose={this.handleCancel}
                    maskClosable={false}
                    width={isMobile ? '100%' : '80%'}
                    bodyStyle={{
                        padding: isMobile ? 8 : 12,
                        background: '#f5f7fa',
                    }}
                    extra={
                        <Space
                            size={6}
                            style={{
                                flexWrap: 'nowrap',
                            }}
                        >
                            <ReceiptHistoryButton onClick={this.openHistory} />
                            {checkPermission('receipt', 'update') &&
                                currentReceipt?.collection_purpose !== 'DEPOSIT' &&
                                !isFullyRefunded ? (
                                <Button
                                    type="primary"
                                    onClick={() => this.toggleOpenForm(true)}
                                    icon={<EditOutlined />}
                                >
                                    Sửa phiếu thu
                                </Button>
                            ) : null}

                            {checkPermission('customer_refund', 'execute') &&
                                Number(refundSummary.remaining_amount) > 0 ? (
                                <Button
                                    danger
                                    onClick={() => this.toggleOpenRefund(true)}
                                    icon={<UndoOutlined />}
                                >
                                    Hoàn tiền
                                </Button>
                            ) : null}

                            {/* Lazy Print: chỉ mount PrintTable khi user muốn in */}
                            <div
                                onMouseEnter={this.ensurePrintRendered}
                                onFocus={this.ensurePrintRendered}
                            >
                                <PrintButton>
                                    {shouldRenderPrint ? (
                                        <PrintTable
                                            customer={customer}
                                            tableData={
                                                currentReceipt?.items || []
                                            }
                                        />
                                    ) : (
                                        <span />
                                    )}
                                </PrintButton>
                            </div>
                        </Space>
                    }
                >
                    {visible ? (
                        <Spin tip="Loading..." spinning={loadingDetail}>
                            <div className="wrap-customer-detail receipt-detail-v2">
                                {/* 1. NHẬN DIỆN PHIẾU THU */}
                                <Card
                                    bordered={false}
                                    style={sectionCardStyle}
                                    bodyStyle={cardBodyStyle}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: 8,
                                            marginBottom: 6,
                                        }}
                                    >
                                        <div
                                            style={{
                                                minWidth: 0,
                                                flex: 1,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    marginBottom: 1,
                                                    fontSize: 13,
                                                    lineHeight: '19px',
                                                    color:
                                                        'rgba(0, 0, 0, 0.45)',
                                                }}
                                            >
                                                Khách hàng
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: isMobile
                                                        ? 20
                                                        : 22,
                                                    lineHeight: '30px',
                                                    fontWeight: 700,
                                                    color:
                                                        'rgba(0, 0, 0, 0.88)',
                                                }}
                                            >
                                                {currentReceipt?.customer
                                                    ?.full_name || '-'}
                                            </div>

                                            <Space
                                                size={[5, 4]}
                                                wrap
                                                style={{
                                                    marginTop: 4,
                                                }}
                                            >
                                                {currentReceipt?.customer
                                                    ?.code ? (
                                                    <Paragraph
                                                        style={{
                                                            margin: 0,
                                                        }}
                                                        copyable={{
                                                            text:
                                                                currentReceipt
                                                                    ?.customer
                                                                    ?.code,
                                                            tooltips: false,
                                                        }}
                                                    >
                                                        <Tag
                                                            style={{
                                                                margin: 0,
                                                                fontSize: 13,
                                                            }}
                                                        >
                                                            Mã KH:{' '}
                                                            {
                                                                currentReceipt
                                                                    ?.customer
                                                                    ?.code
                                                            }
                                                        </Tag>
                                                    </Paragraph>
                                                ) : null}

                                                {currentReceipt?.code && (
                                                    <Tag
                                                        color="blue"
                                                        style={{
                                                            margin: 0,
                                                            fontSize: 13,
                                                        }}
                                                    >
                                                        Mã phiếu:{' '}
                                                        {currentReceipt.code}
                                                    </Tag>
                                                )}

                                                {currentReceipt?.receipt_type === 'service_conversion' &&
                                                    ConvertReceiptType(currentReceipt.receipt_type)}

                                                {/* <Tag
                                                    color={isDepositReceipt ? 'gold' : 'cyan'}
                                                    style={{ margin: 0, fontSize: 13 }}
                                                >
                                                    Kết quả thu tiền:{' '}
                                                    <strong>
                                                        {renderReceiptCollectionResult(
                                                            currentReceipt?.collection_purpose,
                                                            currentReceipt?.settlement_result
                                                        )}
                                                    </strong>
                                                </Tag> */}
                                            </Space>
                                        </div>

                                        <div
                                            style={{
                                                flexShrink: 0,
                                                textAlign: isMobile
                                                    ? 'left'
                                                    : 'right',
                                            }}
                                        >
                                            {renderReceiptStatus(
                                                currentReceipt?.status
                                            )}
                                        </div>
                                    </div>
                                </Card>

                                {hasRefund && (
                                    <Alert
                                        type={isFullyRefunded ? 'error' : 'warning'}
                                        style={{ marginBottom: 10 }}
                                        message={isFullyRefunded
                                            ? 'Phiếu thu đã hoàn toàn bộ'
                                            : 'Phiếu thu đã hoàn một phần'}
                                        description={(
                                            <div>
                                                <div>
                                                    Đã hoàn <strong>{renderMoney(refundSummary.refunded_amount)}</strong>
                                                    {' '}trên tổng tiền phiếu <strong>{renderMoney(refundSummary.receipt_amount)}</strong>.
                                                    {!isFullyRefunded && <> Còn lại <strong>{renderMoney(refundSummary.remaining_amount)}</strong> chưa hoàn.</>}
                                                </div>
                                                <div style={{ marginTop: 4 }}>
                                                    {isFullyRefunded
                                                        ? 'Nghiệp vụ tài chính của phiếu đã hoàn tất; phiếu không được chỉnh sửa.'
                                                        : 'Chỉ được sửa ghi chú và ảnh chứng từ. Số tiền, dịch vụ, ưu đãi, khách hàng, ngày thu, cơ sở và hình thức thu không được sửa trực tiếp; hãy dùng Hoàn tiền / Điều chỉnh.'}
                                                </div>
                                                {refundSummary.last_refund_at && (
                                                    <div style={{ marginTop: 4 }}>
                                                        Lần hoàn gần nhất: <strong>{isValidDate(refundSummary.last_refund_at, true)}</strong>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    />
                                )}

                                {/* 2. TỔNG QUAN TÀI CHÍNH - ƯU TIÊN CAO NHẤT */}
                                <Card
                                    bordered={false}
                                    style={sectionCardStyle}
                                    bodyStyle={cardBodyStyle}
                                >
                                    {this.renderSectionHeader(
                                        'Tổng quan tài chính',
                                        'Các số liệu cần kiểm tra nhanh trên phiếu thu'
                                    )}

                                    <Row gutter={[8, 8]} align="stretch">
                                        <Col xs={24} sm={12} lg={6}>
                                            {this.renderFinancialMetric(
                                                'Doanh số',
                                                renderMoney(
                                                    currentReceipt?.adjusted_sales
                                                ),
                                                {
                                                    primary: true,
                                                }
                                            )}
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            {this.renderFinancialMetric(
                                                'Số tiền thu',
                                                renderMoney(
                                                    currentReceipt?.amount_paid
                                                ),
                                                {
                                                    primary: true,
                                                }
                                            )}
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            {this.renderFinancialMetric(
                                                'Tổng đã thu',
                                                renderMoney(
                                                    currentReceipt?.total_amount_paid
                                                ),
                                                {
                                                    primary: true,
                                                }
                                            )}
                                        </Col>
                                        {refundSummary.refunded_amount > 0 && (
                                            <Col xs={24} sm={12} lg={6}>
                                                {this.renderFinancialMetric(
                                                    'Tổng tiền đã hoàn',
                                                    renderMoney(refundSummary.refunded_amount),
                                                    { danger: hasRefund }
                                                )}
                                            </Col>
                                        )}
                                        {/* {refundSummary.remaining_amount > 0 && (
                                            <Col xs={24} sm={12} lg={6}>
                                                {this.renderFinancialMetric(
                                                    'Còn lại chưa hoàn',
                                                    renderMoney(refundSummary.remaining_amount),
                                                    { primary: true }
                                                )}
                                            </Col>
                                        )} */}


                                        {/* <Col xs={24} sm={12} lg={8} xl={6}>
                                            {this.renderFinancialMetric(
                                                isDepositReceipt
                                                    ? 'Tiền cọc đã sử dụng'
                                                    : 'Đã phân bổ từ phiếu',
                                                renderMoney(
                                                    isDepositReceipt
                                                        ? depositUsed
                                                        : currentReceipt?.allocated_amount
                                                ),
                                                {
                                                    primary: true,
                                                }
                                            )}
                                        </Col> */}

                                        {/* <Col xs={24} sm={12} lg={8} xl={6}>
                                            {this.renderFinancialMetric(
                                                isDepositReceipt
                                                    ? 'Tiền cọc còn lại'
                                                    : 'Số dư còn lại của phiếu',
                                                renderMoney(
                                                    isDepositReceipt
                                                        ? depositRemainingBalance
                                                        : currentReceipt?.unallocated_amount
                                                ),
                                                {
                                                    primary: true,
                                                }
                                            )}
                                        </Col> */}

                                        <Col xs={24} sm={12} lg={8} xl={6}>
                                            {this.renderFinancialMetric(
                                                'Công nợ hiện tại',
                                                renderMoney(currentDebt),
                                                {
                                                    primary: true,
                                                    danger: currentDebt > 0,
                                                }
                                            )}
                                        </Col>
                                        {refundSummary.refunded_amount > 0 && (
                                            <Col xs={24} sm={12} lg={6}>
                                                {this.renderFinancialMetric(
                                                    'Trạng thái hoàn tiền',
                                                    isFullyRefunded ? 'Đã hoàn toàn bộ' : hasRefund ? 'Đã hoàn một phần' : 'Chưa hoàn tiền',
                                                    { danger: hasRefund, strong: true }
                                                )}
                                            </Col>
                                        )}

                                    </Row>
                                    {debtAtReceipt !== currentDebt && (
                                        <div
                                            style={{
                                                marginTop: 10,
                                                color: 'rgba(0, 0, 0, 0.65)',
                                            }}
                                        >
                                            Công nợ ngay sau khi thu:{' '}
                                            <strong>
                                                {renderMoney(debtAtReceipt)}
                                            </strong>
                                        </div>
                                    )}
                                </Card>

                                {(contentRows.length > 0 ||
                                    receiptNote ||
                                    receiptImages.length > 0) && (
                                        <Card
                                            bordered={false}
                                            style={sectionCardStyle}
                                            bodyStyle={cardBodyStyle}
                                        >
                                            {this.renderSectionHeader(
                                                'Dịch vụ và nội dung thu',
                                                'Chi tiết sản phẩm, dịch vụ và nội dung ghi trên phiếu'
                                            )}

                                            {contentRows?.length ? (
                                                <Table
                                                    size="small"
                                                    rowKey={this.rowKeyItems}
                                                    tableLayout="fixed"
                                                    columns={this.contentColumns}
                                                    dataSource={contentRows}
                                                    pagination={false}
                                                    scroll={{ x: "max-content" }}
                                                />
                                            ) : (
                                                <Empty
                                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                    description="Không có nội dung"
                                                />
                                            )}

                                            {(receiptNote ||
                                                receiptImages.length > 0) && (
                                                    <div
                                                        style={{
                                                            marginTop: contentRows.length
                                                                ? 12
                                                                : 0,
                                                            paddingTop: contentRows.length
                                                                ? 12
                                                                : 0,
                                                            borderTop: contentRows.length
                                                                ? '1px solid #f0f0f0'
                                                                : 'none',
                                                        }}
                                                    >
                                                        {receiptNote && (
                                                            <div>
                                                                <div
                                                                    style={{
                                                                        marginBottom: 4,
                                                                        color:
                                                                            'rgba(0, 0, 0, 0.45)',
                                                                    }}
                                                                >
                                                                    Ghi chú
                                                                </div>
                                                                <div>
                                                                    {ReactHtmlParser(
                                                                        receiptNote
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {receiptNote &&
                                                            receiptImages.length >
                                                            0 && (
                                                                <Divider
                                                                    style={{
                                                                        margin:
                                                                            '10px 0',
                                                                    }}
                                                                />
                                                            )}

                                                        {!!receiptImages.length && (
                                                            <div>
                                                                <div
                                                                    style={{
                                                                        marginBottom: 6,
                                                                        color:
                                                                            'rgba(0, 0, 0, 0.45)',
                                                                    }}
                                                                >
                                                                    Ảnh phiếu
                                                                </div>
                                                                <ImagePreviewList
                                                                    images={
                                                                        receiptImages
                                                                    }
                                                                    max={10}
                                                                    thumbSize={60}
                                                                    initialShowAll={
                                                                        false
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                        </Card>
                                    )}

                                {(balanceUsed > 0 ||
                                    (!isDepositReceipt && depositUsed > 0)) && (
                                        <Card
                                            bordered={false}
                                            style={sectionCardStyle}
                                            bodyStyle={cardBodyStyle}
                                        >
                                            {this.renderSectionHeader(
                                                'Số dư / tiền cọc đã sử dụng',
                                                'Các nguồn tiền có sẵn của khách được dùng cùng phiếu này'
                                            )}
                                            <Row gutter={[8, 8]}>
                                                {balanceUsed > 0 && (
                                                    <Col xs={24} sm={12}>
                                                        {this.renderFinancialMetric(
                                                            'Số dư khách hàng đã dùng',
                                                            renderMoney(
                                                                balanceUsed
                                                            ),
                                                            { positive: true }
                                                        )}
                                                    </Col>
                                                )}
                                                {!isDepositReceipt &&
                                                    depositUsed > 0 && (
                                                        <Col xs={24} sm={12}>
                                                            {this.renderFinancialMetric(
                                                                'Tiền cọc đã dùng',
                                                                renderMoney(
                                                                    depositUsed
                                                                ),
                                                                { positive: true }
                                                            )}
                                                        </Col>
                                                    )}
                                            </Row>
                                        </Card>
                                    )}

                                {hasRefund && (
                                    <Card
                                        bordered={false}
                                        style={sectionCardStyle}
                                        bodyStyle={cardBodyStyle}
                                    >
                                        {this.renderSectionHeader(
                                            'Lịch sử hoàn tiền'
                                        )}
                                        {(refundSummary.history || []).map(item => (
                                            <div key={item.refund_id} style={{ marginTop: 8 }}>
                                                <strong>{isValidDate(item.refund_at, true)}</strong>
                                                {' · '}{renderMoney(item.amount)}
                                                {item.actor_name ? ` · ${item.actor_name}` : ''}
                                                {item.reason ? <div style={{ color: 'rgba(0, 0, 0, 0.65)' }}>{item.reason}</div> : null}
                                            </div>
                                        ))}
                                    </Card>
                                )}

                                {/* 4. THÔNG TIN THU */}
                                <Card
                                    bordered={false}
                                    style={sectionCardStyle}
                                    bodyStyle={cardBodyStyle}
                                >
                                    {this.renderSectionHeader(
                                        'Thông tin phiếu thu'
                                    )}

                                    <Row gutter={[20, 0]}>
                                        <Col xs={24} md={8}>
                                            {this.renderInfoField(
                                                'Hình thức thu',
                                                (
                                                    currentReceipt?.payment_breakdowns ||
                                                    currentReceipt?.paymentBreakdowns ||
                                                    []
                                                ).length
                                                    ? (
                                                        currentReceipt.payment_breakdowns ||
                                                        currentReceipt.paymentBreakdowns
                                                    ).map(row => (
                                                        <div
                                                            key={
                                                                row.id ||
                                                                row.payment_method
                                                            }
                                                        >
                                                            {renderPaymentMethod(
                                                                row.payment_method
                                                            )}{' '}
                                                            <strong>
                                                                {renderMoney(
                                                                    row.amount
                                                                )}
                                                            </strong>
                                                        </div>
                                                    ))
                                                    : renderPaymentMethod(
                                                        currentReceipt?.payment_method
                                                    )
                                            )}
                                        </Col>

                                        <Col xs={12} md={6}>
                                            {this.renderInfoField(
                                                'Loại thanh toán',
                                                renderReceiptPaymentType(
                                                    currentReceipt?.payment_type
                                                )
                                            )}
                                        </Col>

                                        <Col xs={12} md={4}>
                                            {this.renderInfoField(
                                                'Ngày thu',
                                                isValidDate(
                                                    currentReceipt?.payment_at,
                                                    false
                                                )
                                            )}
                                        </Col>

                                        <Col xs={12} md={6}>
                                            {this.renderInfoField(
                                                'Cơ sở thu tiền',
                                                currentReceipt?.branch?.name ||
                                                '—'
                                            )}
                                        </Col>
                                        {currentReceipt?.product_amount !== null
                                            && currentReceipt?.product_amount !== undefined && (
                                                <Col xs={24} sm={12} lg={6}>
                                                    {this.renderFinancialMetric(
                                                        <Tooltip title="Là phần Số tiền thu được chia theo tỷ trọng công nợ sản phẩm trên tổng công nợ sản phẩm và dịch vụ đang tham gia. Phần này không vượt công nợ sản phẩm; bên trong nhóm sản phẩm vẫn thanh toán lần lượt các khoản còn nợ theo thứ tự phát sinh.">
                                                            <span>
                                                                Thanh toán sản phẩm{' '}
                                                                <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                                                            </span>
                                                        </Tooltip>,
                                                        renderMoney(currentReceipt.product_amount),
                                                        { primary: true }
                                                    )}
                                                </Col>
                                            )}
                                        {currentReceipt?.service_amount_paid !== null
                                            && currentReceipt?.service_amount_paid !== undefined && (
                                                <Col xs={24} sm={12} lg={6}>
                                                    {this.renderFinancialMetric(
                                                        <Tooltip title="Là phần Số tiền thu được chia theo tỷ trọng công nợ dịch vụ trên tổng công nợ sản phẩm và dịch vụ đang tham gia. Phần này được phân bổ theo tỷ trọng giữa các dịch vụ, có giới hạn công nợ và tự chia lại phần dư; tiền vượt toàn bộ nghĩa vụ mới vào Số dư khách hàng.">
                                                            <span>
                                                                Tiền dành cho dịch vụ{' '}
                                                                <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                                                            </span>
                                                        </Tooltip>,
                                                        renderMoney(currentReceipt.service_amount_paid),
                                                        { primary: true }
                                                    )}
                                                </Col>
                                            )}

                                        {currentReceipt?.commission_processing?.status && (
                                            <Col xs={12} md={6}>
                                                {this.renderInfoField(
                                                    'Trạng thái hoa hồng',
                                                    this.renderCommissionProcessingStatus(
                                                        currentReceipt.commission_processing.status
                                                    ),
                                                    { compact: true }
                                                )}
                                            </Col>
                                        )}

                                        {currentReceipt?.paper_receipt_number && (
                                            <Col xs={12} md={6}>
                                                {this.renderInfoField(
                                                    'Số phiếu giấy',
                                                    currentReceipt.paper_receipt_number
                                                )}
                                            </Col>
                                        )}

                                        <Col xs={24} md={12}>
                                            {this.renderInfoField(
                                                'Lập phiếu',
                                                <CreatedAtCell
                                                    createdAt={
                                                        currentReceipt?.created_at
                                                    }
                                                    user={
                                                        currentReceipt?.created_user
                                                    }
                                                    layout="horizontal"
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                </Card>

                                {/* 5. THÔNG TIN PHỤ / LỊCH SỬ DÀI */}
                                {hasRelatedDetails && (
                                    <Collapse
                                        bordered
                                        defaultActiveKey={[]}
                                        style={{
                                            marginBottom: 10,
                                            background: '#fff',
                                        }}
                                    >
                                        {!!financialTimeline.length && (
                                            <Panel
                                                header={
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: 8,
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        <span>
                                                            Lịch sử số dư từ
                                                            phiếu này
                                                        </span>
                                                        <Tag
                                                            style={{
                                                                margin: 0,
                                                            }}
                                                        >
                                                            {
                                                                financialTimeline.length
                                                            }{' '}
                                                            biến động
                                                        </Tag>
                                                    </div>
                                                }
                                                key="financial-history"
                                            >
                                                <Table
                                                    size="small"
                                                    pagination={{
                                                        pageSize: 10,
                                                    }}
                                                    rowKey="timeline_key"
                                                    tableLayout="fixed"
                                                    dataSource={
                                                        financialTimeline
                                                    }
                                                    columns={
                                                        this
                                                            .financialTimelineColumns
                                                    }
                                                    scroll={{
                                                        x: 1220,
                                                    }}
                                                />
                                            </Panel>
                                        )}

                                        {!!currentReceipt?.transfer_items
                                            ?.length && (
                                                <Panel
                                                    header={
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: 8,
                                                                flexWrap: 'wrap',
                                                            }}
                                                        >
                                                            <span>
                                                                Phiếu chuyển đổi
                                                                dịch vụ
                                                            </span>
                                                            <Tag
                                                                style={{
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                {
                                                                    currentReceipt
                                                                        .transfer_items
                                                                        .length
                                                                }{' '}
                                                                mục
                                                            </Tag>
                                                        </div>
                                                    }
                                                    key="transfer-items"
                                                >
                                                    <Table
                                                        size="small"
                                                        rowKey={
                                                            this.rowKeyTransferItems
                                                        }
                                                        tableLayout="fixed"
                                                        columns={
                                                            this.columnsTransfer
                                                        }
                                                        locale={{
                                                            emptyText:
                                                                'Chưa có dữ liệu',
                                                        }}
                                                        dataSource={
                                                            currentReceipt?.transfer_items ||
                                                            []
                                                        }
                                                        pagination={false}
                                                        scroll={{
                                                            x: 1027,
                                                        }}
                                                    />
                                                </Panel>
                                            )}
                                    </Collapse>
                                )}
                            </div>
                        </Spin>
                    ) : null}
                </Drawer>

                <ReceiptForm
                    visible={isOpenForm}
                    currentReceipt={currentReceipt}
                    reloadData={this.loadData}
                    onCancel={() => this.toggleOpenForm(false)}
                />

                <CustomerServiceConvertForm
                    visible={isOpenConvertForm}
                    currentService={currentService}
                    currentReceipt={currentReceipt}
                    reloadData={this.loadData}
                    onCancel={() => this.toggleOpenConvertForm(false)}
                />

                <CustomerRefundModal
                    visible={this.state.isOpenRefund}
                    customerId={currentReceipt?.customer_id}
                    receiptId={currentReceipt?.id}
                    receiptCode={currentReceipt?.code}
                    onCancel={() => this.toggleOpenRefund(false)}
                    onCompleted={this.loadData}
                />
                <ReceiptHistoryDrawer
                    visible={this.state.isOpenHistory}
                    loading={this.state.historyLoading}
                    items={this.state.historyItems}
                    onClose={() => this.setState({ isOpenHistory: false })}
                />
            </>
        );
    }
}

function mapStateToProps(state) {
    return {
        authUser: state.auth.authUser,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        getSpecificService: id => dispatch(getSpecificService(id)),
        getReceiptHistory: id => dispatch(getReceiptHistory(id)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Form.create({ name: 'ReceiptDetail' })(ReceiptDetail));
